import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import styles from './style.module.css'
import { getSchema, generateCreditLimitList } from './utils'
import { Button, Heading, Loader, Text, useDesignLanguagePerClass } from '@afterpaytouch/core'
import FormBuilder, { DropdownItems } from '@afterpaytouch/form'
import { useCheckoutSizes } from '../../utils/responsive'
import { EmploymentDetailsForm } from './types'
import { Route } from '../../config/router'
import {
  selectPurchaseMethod,
  setCheckoutStatus,
  setSelectedPaymentType,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useCheckoutState,
  useCheckoutToken,
  useCreateFacilityAssessmentMutation,
  usePBIRejected,
  usePreAssessmentMutation,
  useProfilingId,
} from '../../state'
import { CheckoutStatus, money } from '../../model'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { PaymentType } from '@afterpay/types'
import { useMoney } from '../Money/useMoney'

export const EmploymentDetails: FunctionComponent = () => {
  const { t } = useTranslation()
  const { headingSize } = useCheckoutSizes()
  const token = useCheckoutToken()
  const profilingSessionId = useProfilingId()
  const [lock, setLock] = useState<boolean>(false)
  const [isFormReady, setIsFormReady] = useState(false)
  const [creditLimitList, setCreditLimitList] = useState<DropdownItems[]>([])
  const router = useAppRouter()
  const state = useCheckoutState()
  const hasPBIRejected = usePBIRejected()
  const { locale } = useAppRouter()
  const dispatch = useAppDispatch()
  const paymentMethod = useAppropriatePaymentMethod()
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const [createFacilityAssessment, facilityAssessmentResult] = useCreateFacilityAssessmentMutation({
    fixedCacheKey: 'shared-facility-assessment',
  })
  const [_, { data: preAssessmentData }] = usePreAssessmentMutation({ fixedCacheKey: 'shared-pre-assessment' })
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const orderAmount = state.started?.consumerAmount ?? state.checkout?.amount
  const { formatMoney } = useMoney({
    locale,
    currency: orderAmount?.currency,
  })
  const shouldShowLoader =
    facilityAssessmentResult.isLoading || facilityAssessmentResult.isSuccess || facilityAssessmentResult.isError || creditLimitList.length === 0

  useEffect(() => {
    // Reset mutation cache for this endpoint
    facilityAssessmentResult.reset()
  }, [])

  const handleStateChange = (isValid: boolean): void => {
    setIsFormReady(isValid)
  }

  useEffect(() => {
    if (orderAmount != null && preAssessmentData?.facilityLimit != null && formatMoney && creditLimitList.length === 0) {
      const creditLimitList: DropdownItems[] = generateCreditLimitList(orderAmount, preAssessmentData?.facilityLimit, formatMoney)
      logEvent(TrackingEvent.VIEWED_CL_CREDIT_EMPLOYMENT_DETAILS_PAGE, { creditLimitList })
      setCreditLimitList(creditLimitList)
    }
  }, [orderAmount, preAssessmentData?.facilityLimit, formatMoney, creditLimitList])

  const onSubmit = async (employmentDetailsFormData: EmploymentDetailsForm): Promise<void> => {
    setLock(true)
    logEvent(TrackingEvent.PRESSED_CL_CREDIT_EMPLOYMENT_DETAILS_PAGE_CONTINUE_BUTTON)
    const params = {
      ...employmentDetailsFormData,
      token,
      maxFacilityLimit: money(employmentDetailsFormData.maxFacilityLimit, orderAmount?.currency),
      profilingSessionId,
    }

    try {
      await createFacilityAssessment(params).unwrap()
      router.push(Route.APPROVED_CREDIT)
    } catch (e) {
      if (!hasPBIRejected) {
        dispatch(setSelectedPaymentType(PaymentType.PBI))
        if (paymentMethod !== null) {
          dispatch(selectPurchaseMethod(paymentMethod))
        } else {
          dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
          router.push(Route.ADD_PAYMENT_METHOD)
          return
        }
        router.push(Route.SUMMARY)
      } else {
        router.push(Route.TERMINAL_ERROR)
      }
    }
  }
  const { schema } = useMemo(() => {
    const components = {
      creditLimitText: () => {
        return (
          <Text size='XS' color='Neutral 600'>
            {t('consumerLending:employmentDetails:creditLimitText')}
          </Text>
        )
      },
      upcomingExpensesText: () => {
        return <Text>{t('consumerLending:employmentDetails:upcomingExpenses')}</Text>
      },
      footer: () => {
        return (
          <Button.Primary
            loading={facilityAssessmentResult.isLoading}
            size='Large'
            type='submit'
            disabled={!isFormReady || lock}
            padding='Fluid'
            testNameSpace='employment-details'
          >
            {t('consumerLending:employmentDetails:continue')}
          </Button.Primary>
        )
      },
    }

    return getSchema(components, t, logEvent, creditLimitList)
  }, [lock, isFormReady, logEvent, locale, headingSize, t, creditLimitList])

  return (
    <>
      {shouldShowLoader ? (
        <Loader text={t('consumerLending:preAssessment:loader')} />
      ) : (
        <div className={designLanguageStyles.container}>
          <Heading size={headingSize}>{t(`consumerLending:employmentDetails:heading`)}</Heading>
          <Text>{t('consumerLending:employmentDetails:headingSubText')}</Text>
          <FormBuilder schema={schema} testNameSpace='employment-details' onSubmit={onSubmit} onStateChange={handleStateChange} disabled={lock} />
        </div>
      )}
    </>
  )
}
